import dayjs from 'dayjs';
import { Button, Status } from 'plume-ui';
import {
  DataRow,
  Heading,
  SortDirection,
} from 'plume-ui/dist/components/Table/Table';
import { STANDARD_DATE_TIME_FORMAT } from '../../../../config';
import { ConfigurationInfoModalData, Maybe } from '../../../../types';
import FormattedMessage from '../../../../utils/components/FormattedMessage';
import i18n from '../../../../utils/i18n';
import { mapWebhookStatusToColor } from './helpers';
import ActionCell from './renderers/tableCellAction';
import { Webhook, WebhookCredentials, WebhookStatus } from './types';

export const EmptyWebhookObject: Webhook = {
  name: '',
  authType: 'basicAuth',
  urlEndpoint: '',
  credentials: {
    username: '',
    password: '',
  },
  enabled: true,
};

export const defaultWebhookCredentials: WebhookCredentials = {
  username: '',
  password: '',
};

export const webhookListTableHeaders = ({
  webhookList,
  openOutreachListModal,
  openErrorDetailsModal,
  editWebhook,
  deleteWebhook,
  setEnabled,
  editAllowed,
}: {
  webhookList: WebhookStatus[];
  openOutreachListModal: (data: Maybe<ConfigurationInfoModalData>) => void;
  openErrorDetailsModal: (data: Maybe<ConfigurationInfoModalData>) => void;
  editWebhook: (id: string) => void;
  deleteWebhook: (id: string) => void;
  setEnabled: (id: string, enabled: boolean) => void;
  editAllowed: boolean;
}): Heading[] => [
  {
    name: i18n.t('configuration.webhooks.name'),
    fieldName: 'name',
  },
  {
    name: i18n.t('configuration.webhooks.platformUrl'),
    fieldName: 'platformUrl',
  },
  {
    name: i18n.t('configuration.webhooks.credentials'),
    fieldName: 'credentials',
    render: (rowData: DataRow) => (
      <div className="Configuration__collapseCard__webhook__table__rendered-cell">
        <Status
          color={mapWebhookStatusToColor(rowData.credentials)}
          label={`${rowData.credentials
            .charAt(0)
            .toUpperCase()}${rowData.credentials.slice(1)}`}
        />
      </div>
    ),
  },
  {
    name: i18n.t('configuration.webhooks.lastError'),
    fieldName: 'lastError',
    render: (rowData: DataRow) => (
      <div className="Configuration__collapseCard__webhook__table__rendered-cell">
        {rowData.lastError}
        {rowData.lastError !== '' && (
          <Button
            styleVariant="tertiary"
            onClick={() => {
              openErrorDetailsModal(
                webhookList.find((item) => item.id === rowData.action) as Maybe<
                  ConfigurationInfoModalData
                >,
              );
            }}
          >
            <FormattedMessage id="configuration.webhooks.more" />
          </Button>
        )}
      </div>
    ),
    sortFnGen: (sortDir: SortDirection) => {
      return (a, b) => {
        if (a.lastError === '') return 1;
        if (b.lastError === '') return -1;
        return dayjs(a.lastError, STANDARD_DATE_TIME_FORMAT).valueOf() <
          dayjs(b.lastError, STANDARD_DATE_TIME_FORMAT).valueOf()
          ? sortDir
          : -sortDir;
      };
    },
  },
  {
    name: i18n.t('configuration.webhooks.lastSuccess'),
    fieldName: 'lastSuccess',
    sortFnGen: (sortDir: SortDirection) => {
      return (a, b) => {
        if (a.lastSuccess === '') return 1;
        if (b.lastSuccess === '') return -1;
        return dayjs(a.lastSuccess, STANDARD_DATE_TIME_FORMAT).valueOf() <
          dayjs(b.lastSuccess, STANDARD_DATE_TIME_FORMAT).valueOf()
          ? sortDir
          : -sortDir;
      };
    },
  },
  {
    name: i18n.t('configuration.webhooks.monitors'),
    fieldName: 'supportOutreach',
    render: (rowData: DataRow) => (
      <div className="Configuration__collapseCard__webhook__table__rendered-cell">
        <div className="Configuration__collapseCard__webhook__table__rendered-cell__monitors">
          {rowData.noOfSupportOutreach}
          <Button
            styleVariant="tertiary"
            onClick={() => {
              openOutreachListModal(
                webhookList.find((item) => item.id === rowData.action) as Maybe<
                  ConfigurationInfoModalData
                >,
              );
            }}
          >
            <FormattedMessage id="configuration.webhooks.more" />
          </Button>
        </div>
      </div>
    ),
    sortFnGen: (sortDir: SortDirection) => {
      return (a, b) => {
        return +a.noOfSupportOutreach < +b.noOfSupportOutreach
          ? 1 * sortDir
          : -1 * sortDir;
      };
    },
    sortable: true,
  },
  {
    name: i18n.t('configuration.webhooks.action'),
    fieldName: 'action',
    render: (rowData: DataRow) => (
      <ActionCell
        id={rowData.action}
        edit={editWebhook}
        del={deleteWebhook}
        setEnabled={setEnabled}
        enabled={rowData.enabled === 'true'}
        editAllowed={editAllowed}
      />
    ),
    sortFnGen: (sortDir: SortDirection) => {
      return (a, b) =>
        (a.enabled === 'true') < (b.enabled === 'true') ? sortDir : -sortDir;
    },
  },
];
